import { QuizVariant } from "@outschool/routes";

import { LearnerAttributesType, STEP } from "./consts";

const STORAGE_KEYS = {
  LEARNER_ATTRIBUTES: "onboarding_learner_attributes",
  CURRENT_STEP: "onboarding_current_step",
  ACTIVE_STEPS: "onboarding_active_steps",
  QUIZ_VARIANT: "onboarding_quiz_variant",
} as const;

export const saveOnboardingState = (
  learnerAttributes: LearnerAttributesType,
  currentStep: number,
  activeSteps: STEP[],
): void => {
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.LEARNER_ATTRIBUTES,
      JSON.stringify(learnerAttributes),
    );
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, currentStep.toString());
    sessionStorage.setItem(
      STORAGE_KEYS.ACTIVE_STEPS,
      JSON.stringify(activeSteps),
    );
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: { message: "Failed to save onboarding state to session storage" },
    });
  }
};

export const loadOnboardingState = (): {
  learnerAttributes: LearnerAttributesType | null;
  currentStep: number | null;
  activeSteps: STEP[] | null;
} => {
  try {
    const learnerAttributesStr = sessionStorage.getItem(
      STORAGE_KEYS.LEARNER_ATTRIBUTES,
    );
    const currentStepStr = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
    const activeStepsStr = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_STEPS);

    const learnerAttributes = learnerAttributesStr
      ? JSON.parse(learnerAttributesStr)
      : null;
    const currentStep = currentStepStr ? parseInt(currentStepStr, 10) : null;
    const activeSteps = activeStepsStr ? JSON.parse(activeStepsStr) : null;

    return {
      learnerAttributes,
      currentStep,
      activeSteps,
    };
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: {
        message: "Failed to load onboarding state from session storage",
      },
    });
    return {
      learnerAttributes: null,
      currentStep: null,
      activeSteps: null,
    };
  }
};

export const clearOnboardingState = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.LEARNER_ATTRIBUTES);
    sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
    sessionStorage.removeItem(STORAGE_KEYS.ACTIVE_STEPS);
    sessionStorage.removeItem(STORAGE_KEYS.QUIZ_VARIANT);
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: {
        message: "Failed to clear onboarding state from session storage",
      },
    });
  }
};

export const saveActiveSteps = (activeSteps: STEP[]): void => {
  try {
    sessionStorage.setItem(
      STORAGE_KEYS.ACTIVE_STEPS,
      JSON.stringify(activeSteps),
    );
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: { message: "Failed to save active steps to session storage" },
    });
  }
};

export const loadActiveSteps = (): STEP[] | null => {
  try {
    const activeStepsStr = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_STEPS);
    return activeStepsStr ? JSON.parse(activeStepsStr) : null;
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: { message: "Failed to load active steps from session storage" },
    });
    return null;
  }
};

export const saveQuizVariant = (variant: QuizVariant): void => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.QUIZ_VARIANT, variant);
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: { message: "Failed to save quiz variant to session storage" },
    });
  }
};

export const loadQuizVariant = (): QuizVariant | null => {
  try {
    const variant = sessionStorage.getItem(STORAGE_KEYS.QUIZ_VARIANT);
    return variant as QuizVariant | null;
  } catch (error) {
    OsPlatform.captureError(error, {
      extra: { message: "Failed to load quiz variant from session storage" },
    });
    return null;
  }
};
